import { FilterOperators, Messages } from "@crispico/foundation-gwt-js";
import { CrudSettings } from "@crispico/foundation-react/entity_crud/CrudSettings";
import { roleToUserEntityDescriptor } from "@crispico/foundation-react/pages/role/roleEntityDescriptor";
import { ColorRegistry, ColorRegistryRRC } from "@crispico/foundation-react/utils/ColorRegistry";
import { configureStore, EnhancedStore } from "@reduxjs/toolkit";
import { Drawer } from "antd";
import { QueryOptions } from "apollo-client";
import { ConnectedRouter, connectRouter, push, routerMiddleware } from "connected-react-router";
import { DocumentNode } from "graphql";
import gql from "graphql-tag";
import { createHashHistory, createLocation, History, Location, UnregisterCallback } from "history";
import Interweave from "interweave";
import lodash from 'lodash';
import moment from "moment-timezone";
import React, { ReactElement, ReactNode, Suspense } from 'react';
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { Link, matchPath, Redirect, Switch } from 'react-router-dom';
import { combineReducers } from "redux";
import thunk from "redux-thunk";
import { Button, Container, Dimmer, Dropdown, Header, Icon, Loader, Menu, Message, Modal, SemanticICONS, Popup, CheckboxProps, Divider, Segment } from "semantic-ui-react";
import { logout } from "./apollo-gen-foundation/logout";
import { apolloClient, apolloClientHolder, initApolloClient } from "./apolloClient";
import { AppMetaTempGlobals } from "./AppMetaTempGlobals";
import { CompMeta, Optional } from "./CompMeta";
import { ColumnConfigDropdown, currentColumnConfigId, currentEditorColumnConfigId, sliceColumnConfigDropdown } from "./components/ColumnConfig/ColumnConfigDropdown";
import { columnConfigEntityDescriptor } from "./components/ColumnConfig/ColumnConfigEntityDescriptor";
import { ContainerWithHeader, ContainerWithHeaderContext } from "./components/containerWithHeader/ContainerWithHeader";
import { currentCustomQueryId } from "./components/CustomQuery/CustomQueryDropdown";
import { customQueryEntityDescriptor } from "./components/CustomQuery/CustomQueryEntityDescriptor";
import { Filter } from "./components/CustomQuery/Filter";
import { HorizontalMenu } from "./components/HorizontalMenu/HorizontalMenu";
import { ModalExt, Severity } from "./components/ModalExt/ModalExt";
import { addAfterStartupRunnable, entityDescriptors, resetToEntityDescriptorsWithoutCrudSettingsOverride, runAfterStartupRunnables, sessionGlobalReload } from "./entity_crud/entityCrudConstants";
import { EntityDescriptor } from "./entity_crud/EntityDescriptor";
import { EntityDescriptorPopulatorFromEntityGen } from "./entity_crud/EntityDescriptorPopulatorFromEntityGen";
import { SliceEntityEditorPage } from "./entity_crud/EntityEditorPage";
import { FieldType } from "./entity_crud/FieldType";
import { FindByFilterParams } from "./entity_crud/FindByFilterParams";
import "./messages";
import { EntityDescriptorForServerUtils } from "./flower/entityDescriptorsForServer/EntityDescriptorForServerUtils";
// import * as zeroTrainingAudit from './pages/Audit/stories';
import { LOGOUT } from "./pages/loginPage/queries";
import { NotificationComponent, sliceNotificationComponent } from "./pages/notification/NotificationComponent";
import { organizationEntityDescriptor } from "./pages/SettingsEntity/settingsEntityDescriptor";
import { AppRouteProps, ConnectedComponentInSimpleComponent, ConnectedPageHelper, ConnectedPageInfo, createSliceFoundation, getBaseImpures, getBaseReducers, PropsFrom, RootReducerForPages, StateFrom } from "./reduxHelpers";
import { TestUtils } from "./utils/TestUtils";
import { ShowGlobalAlertParams, DATABASE_MENU_ENTRY, Utils } from "./utils/Utils";
import { ZeroTrainingArticlesRegistry, ZeroTrainingContext } from "./zeroTraining/dataStructures";
import * as zeroTrainingZt from "./zeroTraining/stories/demoZeroTraining.stories";
import { ZeroTrainingArticlePageRoute } from "./zeroTraining/ZeroTrainingArticlePage";
import { ZeroTrainingIndexPageRoute } from "./zeroTraining/ZeroTrainingIndexPage";
import { ReduxReusableComponents, RRC_SINGLETON } from "./reduxReusableComponents/ReduxReusableComponents";
import _ from "lodash";
import { Clock } from "./components/Clock/Clock";
import { TreeMenu, TreeMenuRRC } from "./components/treeMenu/TreeMenu";
import { TreeMenuSearch } from "./components/treeMenu/TreeMenuSearch";
import { WrapperWithRenderFunction } from "./utils/WrapperWithRenderFunction";
import "@crispico/foundation-react/pages/Audit/auditEntityDescriptor"; // out of the blue, only in prot, this was imported AFTER finalizeDescriptors() was ran => the registered afterStartupRunnable(), wouldn't be ran any more. Hence I force its inclusion here
import { ClientErrorLogger } from "./clientErrorLogger/ClientErrorLogger";
import { ToolkitStore } from "@reduxjs/toolkit/dist/configureStore";
import { AppContainerContext, AppContainerContextValue, ResponsiveMode, ResponsiveState } from "./AppContainerContext";
import { EntityTablePage } from "./entity_crud/EntityTablePage";
import { CombineCheck, ExpandCollapseButtons, getCombineCheck, getFilterLeftBarCheck, getFilterLeftBarOrganizations, NO_ORGANIZATION, OrganizationDropdownValue, OrganizationSelector, OrganizationSelectorRRC, OrganizationTree, OrganizationTreeRoot, OrganizationTreeRRC, setCombineCheck } from "./pages/organization/OrganizationSelector";
import { Scriptable, ScriptableContainer } from "@famiprog-foundation/scriptable-ui";
import { getPermissionLabel } from "./pages/role/permissionsUtils";
import { RoutedModals } from "@famiprog-foundation/foundation-react-core";

/**
 * Used to find the menu entry key & to store in session the forcedMode value.
 */
export const RESPONSIVE_STATE_FORCED_MODE_KEY = 'responsiveState.forcedMode';

let infoCurrentUserEditorPage: any;
addAfterStartupRunnable(() => {
    import("@crispico/foundation-react/pages/user/CurrentUserEditorPage").then(p => {
        infoCurrentUserEditorPage = p.infoCurrentUserEditorPage;
    })
});

export interface FoundationInitializationsForClient {
    version: string[2];
    currentUser: Optional<User>;
    currentRole: Optional<Role>;
    currentPermissions: Optional<any>;
    currentOrganization: Optional<Organization>;
    currentOrganizationDropdownValue: Optional<OrganizationDropdownValue>;
    allOrganizationsAccess: Optional<boolean>;

    visualStyleSettings: VisualStyleSettings;
    homePageSettings: HomePageSettings;
    colorSettings: ColorSettings;
    internationalizationSettings: InternationalizationSettings;
    notificationSettings?: NotificationSettings;
    auditSettings?: AuditSettings;
    userSettings?: UserSettings;
    crudSettings?: CrudSettings;
    organizationSettings?: OrganizationSettings;
    oktaAuthenticationAvailable?: boolean;
    rememberMeEnabled?: boolean;
    ldapAvailable: boolean;
    fileBrowserBulkDownloadMaximumFileSize: number;
    tempSettings?: TempSettings;
}

export interface User {
    id: number;
    username: string;
    firstName: Optional<string>;
    lastName: Optional<string>;
    isAdmin: boolean;
    language: Optional<string>
}

export interface Role {
    id: any;
    name: string;
    permissions: String;
    isAdmin: boolean;
}

export interface Organization {
    id: any;
    name: string;
    qualifiedName: string;
    parent?: Organization;
    children?: Organization[];
}

export enum OrganizationFilter {
    NONE = "NONE", LEFT_SIDE = "LEFT_SIDE"
};
export interface OrganizationSettings {
    globalOrganizationFilter: OrganizationFilter;
}

export interface VisualStyleSettings {
    appTitle?: string;
    headerLogo?: string;
    headerLogoRight?: boolean;
    clientHeaderLogo?: string;
    whiteBackgroundHeaderLogo?: boolean;
    whiteBackgroundClientHeaderLogo?: boolean;

    homeLogo?: string;
    homeTitle?: string;
    homeBackgroundImage?: string;
    homeLogo2?: string;
}

export interface HomePageSettings {
    usePersonalHomePage: boolean;
    footerBarText: string;
    defaultPage: string;
    defaultPageMobile: string,
    homePageDashboards: [{ id: number }]
}

export interface ColorSettings {
    data: Array<Color>
}

export interface Color {
    name: string;
    value?: number;
    thisColorIsAliasFor?: string
}

export interface NotificationSettings {
    maxNumberOfNotificationDisplayed: number;
    refreshRate: number;
}

export interface TempSettings {
    historyGraphInitialFields: string;
}

export interface AuditSettings {
    maxRowsForPopup: number;
}

export interface UserSettings {
    passwordMinLength: number;
    passwordMaxLength: number;
    passwordHasUpperCaseConstraint: boolean;
    passwordHasLowerCaseConstraint: boolean;
    passwordHasNumberConstraint: boolean;
    passwordHasSpecialCharacterConstraint: boolean;
}

export type MenuEntry = {
    id: number,
    name: string,
    icon: string,
    color: string
}

export interface InternationalizationSettings {
    showBothOriginalAndTranslated: boolean;
    translatableByUserLanguagesMap: {
        [language: string]: {
            entriesMap: { [original: string]: string }
        }
    },
    dateFormat?: string,
    timeFormat?: string,
    dateFormats?: [
        { name: string, momentJsFormat: string, momentJsFormatShorter: string }
    ]
    timeFormats?: [
        { name: string, momentJsFormat: string, momentJsFormatShorter: string }
    ],
    defaultLanguage: string
}

export interface ShowCrudButtons {
    showImportButton: boolean,
    showExportButton: boolean,
    showShareLinkButton: boolean,
    showCustomQueryButton: boolean,
    showCustomQueryEmailSchedule: boolean,
    showColumnConfigButton: boolean,
    showAuditButton: boolean,
    showExportAsCsvButton: boolean,
}

export enum EntityFormLightOpenType { MODAL, DRAWER };

const menuEntries = [] as any[];
var menuInitialExpandedIds = {} as { [key: string]: boolean; };
export const searchOptions = {} as { [key: string]: string[] };

/**
 * @author Cristian Spiescu
 */
export class AppMeta {

    componentCreated = false;

    routes: JSX.Element[] = [];

    entityCrudMenus: any[] | undefined;

    reducerCombination: any = {};

    showHorizontalMenu: boolean = false;

    showMyAccountMenuEntry: boolean = true;

    removeMenuEntryWhenNoPermission: boolean = true;

    scrollOnlyContentInEditor: boolean = false;

    showCrudButtons: ShowCrudButtons = {
        showImportButton: true,
        showExportButton: true,
        showShareLinkButton: true,
        showCustomQueryButton: true,
        showCustomQueryEmailSchedule: true,
        showColumnConfigButton: true,
        showAuditButton: true,
        showExportAsCsvButton: true,
    };

    /**
     * SH: Temporary property used to hide "View properties" tab on Proteus.
     */
    showViewPropertiesTab: boolean = true;

    entityFormLightOpenType: EntityFormLightOpenType = EntityFormLightOpenType.MODAL;
    /**
     * SH: By default, for each ED, the property 'hasAttachedDashboards' is true.
     * This property is used to define if dashboards are implemented at the project level.
     */
    dashboardsAvailable: boolean = true;

    newRootReducer = new RootReducerForPages();

    zeroTrainingArticlesRegistry = new ZeroTrainingArticlesRegistry();

    helperAppContainer!: ConnectedPageHelper<typeof sliceAppContainer>;

    globalPermissions: Set<string> = new Set();

    loadOrganizationsQuery!: DocumentNode;

    store!: ToolkitStore;

    // TODO RM35382: De eliminat si actualizat cf. pct 1/
    tempLocationForEditorBackground: Location | undefined = undefined;

    // list of EntityDescriptors used by UI (e.g. for menu entries, role config)
    protected uiEntityDescriptors: Optional<{ [entityName: string]: EntityDescriptor }>;

    protected globalErrorHandler?: ClientErrorLogger;

    get menuInitialExpandedIds() {
        return menuInitialExpandedIds;
    }

    set menuInitialExpandedIds(value) {
        menuInitialExpandedIds = value;
    }

    get menuEntries() {
        return menuEntries;
    }

    set menuEntries(value) {
        menuEntries.splice(0, menuEntries.length);
        Array.prototype.push.apply(menuEntries, value); // this trick to pass an array into the varargs param
    }

    constructor(public metas: CompMeta<any, any>[]) {
        this.globalPermissions.add(DATABASE_MENU_ENTRY);
        if (AppMetaTempGlobals.appMetaInstance) {
            // TODO CC: this doesn't work for storybook
            // throw new Error("Illegal state: an instance of AppHelper already exists")
        }
        AppMetaTempGlobals.appMetaInstance = this;

        this.configQueries();
        if (!Utils.isTest()) {
            Utils.showGlobalAlert = params => {
                // Sometimes when developing, we may generate an error so early, that the React app is not completely setup.
                // And in this case it's shameful to generate another error while trying to display the initial error. 
                // Hence the "fallback" version below
                if (!this.helperAppContainer?.dispatchers) {
                    alert(`Global alert fallback. Severity: ${params.severity}. Title: ${params.title}\nMessage: ${params.message}`);
                    return;
                }
                this.helperAppContainer.dispatchers.showGlobalAlert(params);
            }
        }

        metas.forEach((m, i) => {
            m.contributeRootReducer(this.reducerCombination);
            this.routes.push(m.createRoute());
        });
        this.routes.push(ZeroTrainingIndexPageRoute());
        this.routes.push(ZeroTrainingArticlePageRoute());

        this.zeroTrainingArticlesRegistry.addFromModule(zeroTrainingZt);
        // this.zeroTrainingArticlesRegistry.addFromModule(zeroTrainingAudit);
        this.addEntityDescriptors([customQueryEntityDescriptor]);
        this.addEntityDescriptors([columnConfigEntityDescriptor]);
        // The current user slice extends the user slice that requires the one to many entity descriptors to be defined
        // @see EntityEditorPage.onBeforeMergeByConnectedPageHelper
        this.addEntityDescriptors([roleToUserEntityDescriptor]);

        SliceEntityEditorPage.sliceColumnConfigDropdown = sliceColumnConfigDropdown;
        SliceEntityEditorPage.ColumnConfigDropdown = ColumnConfigDropdown;

        // This method, besides other things, creates the routes described in each entityDescriptor.connectedPageInfo 
        // We need this to execute no matter if a user is currently logged in order for the <PrivateRoutes> to be registered 
        // so that the redirect to login mechanism (when no user logged in) to work 
        this.entityCrudMenus = AppMetaTempGlobals.appMetaInstance.finalizeEntityDescriptors();
        // register the globalErrorHandler
        this.createGlobalErrorHandler();
    }

    showSearchBarOnTitleBar(): boolean {
        const { responsiveState } = this.helperAppContainer.dispatchers.getState().appContainerContextValue;
        return responsiveState.mode === ResponsiveMode.DESKTOP;
    }

    showTimeZoneItem(): boolean {
        const { responsiveState } = this.helperAppContainer.dispatchers.getState().appContainerContextValue;
        return responsiveState.mode === ResponsiveMode.DESKTOP;
    }

    showClockItem(): boolean {
        const { responsiveState } = this.helperAppContainer.dispatchers.getState().appContainerContextValue;
        return responsiveState.mode === ResponsiveMode.DESKTOP;
    }

    showBackButton(): boolean {
        const { responsiveState } = this.helperAppContainer.dispatchers.getState().appContainerContextValue;
        return responsiveState.mode !== ResponsiveMode.DESKTOP;
    }

    showReloadButton(): boolean {
        const { responsiveState } = this.helperAppContainer.dispatchers.getState().appContainerContextValue;
        return responsiveState.mode !== ResponsiveMode.DESKTOP;
    }

    showHomeButton(): boolean {
        const { responsiveState } = this.helperAppContainer.dispatchers.getState().appContainerContextValue;
        return responsiveState.mode !== ResponsiveMode.DESKTOP;
    }

    showNotificationsItem(): boolean {
        return true;
    }

    showOrganizationDropdownItem(): boolean {
        const { responsiveState } = this.helperAppContainer.dispatchers.getState().appContainerContextValue;
        return responsiveState.mode === ResponsiveMode.DESKTOP;
    }

    showHeaderLogoItem(): boolean {
        const { responsiveState } = this.helperAppContainer.dispatchers.getState().appContainerContextValue;
        return responsiveState.mode === ResponsiveMode.DESKTOP;
    }

    // #region client-error-logger-start
    protected createGlobalErrorHandler() {
        this.globalErrorHandler = new ClientErrorLogger().start();
    }
    // #endregion

    /**
     * CC: Temporary property, call or not onLocationChanged from appContainer constructor
     * Is set to false for XopsMobile APK.
    */
    canCallOnLocationChangedFromAppContainerConstructor(): boolean {
        return true;
    }

    addConnectedPageInfos(infos: ConnectedPageInfo[]) {
        for (let info of infos) {
            const helper = new ConnectedPageHelper(info);
            this.newRootReducer.add(helper);
            this.routes.push(helper.getRoute());
        }
    }

    createStore(initialState?: any, history?: History) {
        this.helperAppContainer = new ConnectedPageHelper<typeof sliceAppContainer>(infoAppContainer);
        this.newRootReducer.add(this.helperAppContainer);

        Utils.showSpinner = (spinnerText?: string, showSpinnerCloseButton: boolean = true) => this.helperAppContainer.dispatchers.setInReduxState({ showSpinner: true, spinnerText: spinnerText, showSpinnerCloseButton: showSpinnerCloseButton });
        Utils.hideSpinner = () => this.helperAppContainer.dispatchers.setInReduxState({ showSpinner: false, spinnerCloseConfirmationModalOpen: false });

        if (!this.menuEntries) {
            this.menuEntries = [];
        }

        const middleware: any = [thunk];
        let rc = this.reducerCombination;
        if (history) {
            middleware.push(routerMiddleware(history));
            rc = { ...rc, router: connectRouter(history) };
        }
        return configureStore({
            reducer: this.createRootReducer(rc), preloadedState: initialState,
            enhancers: [ReduxReusableComponents.storeEnhancer],
            middleware: getDefaultMiddleware => [...getDefaultMiddleware(ReduxReusableComponents.enhanceMiddlewareConfig()), ...middleware],
        }) as unknown as EnhancedStore<any, any>;
    }

    createRootReducer(reducerCombination: any) {
        const combinedReducer: any = combineReducers(reducerCombination);
        return (state: any, action: any) => {
            const newState = this.newRootReducer.reducer(state, action);
            // newState.router = rc.router(state?.router, action);

            // temp ca sa scapam de warning/eroare emis de reducerul creat de combineReducers()
            const cleanedUpOldState = !state ? undefined : Object.keys(reducerCombination).reduce((obj, field) => {
                obj[field] = state[field];
                return obj;
            }, {} as any);

            Object.assign(newState, combinedReducer(cleanedUpOldState, action));
            return newState;
        }
    }

    protected async invokeGetInitializationsForClientQuery(options: QueryOptions) {
        return await apolloClientHolder.apolloClient.query(options);
    }

    protected async loadInitializationsForClient_fromConstructor() {
        await this.getEntityDescriptorsForServer();
        this.addCustomFieldsToEntityDescriptors();
        await this.loadInitializationsForClient();
    }

    protected initRRCSingletons() {

    }

    public async loadInitializationsForClient() {
        const query = this.getClientInitializationService_initializationsForClient_query(this.getFieldsForInitializationsForClient());
        const initializationsForClient = (await this.invokeGetInitializationsForClientQuery({ query, context: { showSpinner: false } })).data.clientInitializationService_initializationsForClient as FoundationInitializationsForClient;
        this.populateDescriptorsWithCrudSettings(initializationsForClient.crudSettings);

        document.title = this.getAppTitle(initializationsForClient.visualStyleSettings);

        const dateFormat = initializationsForClient.internationalizationSettings?.dateFormats?.find(f => f.name === initializationsForClient.internationalizationSettings?.dateFormat);
        if (dateFormat) {
            Utils.dateFormat = dateFormat.momentJsFormat;
            Utils.dateFormatShorter = dateFormat.momentJsFormatShorter;
        }
        const timeFormat = initializationsForClient.internationalizationSettings?.timeFormats?.find(f => f.name === initializationsForClient.internationalizationSettings?.timeFormat);
        if (timeFormat) {
            Utils.timeFormat = timeFormat.momentJsFormatShorter;
            Utils.timeWithSecFormat = timeFormat.momentJsFormat;
        }
        Utils.dateTimeFormat = Utils.dateFormat + " " + Utils.timeFormat;
        if (initializationsForClient.colorSettings) {
            ColorRegistry.INSTANCE.init(initializationsForClient.colorSettings);
        }

        Messages.getInstance().setTranslatableByUser({
            // internationalizationSettings currently not available for prot; hence ?
            getShowBothOriginalAndTranslated: () => initializationsForClient.internationalizationSettings?.showBothOriginalAndTranslated,
            get: (language: string, original: string) => {
                const l = initializationsForClient.internationalizationSettings?.translatableByUserLanguagesMap[language];
                if (!l) { return null };
                return l.entriesMap[original];
            }
        });

        this.resetMenuEntries();

        let language = initializationsForClient.internationalizationSettings.defaultLanguage;

        if (initializationsForClient.currentUser?.language) {
            let userSettings = initializationsForClient.crudSettings?.forEntities.find((forEntity) => forEntity.entityName === "User")
            if (userSettings) {
                // Check if the user language exists in fieldIntervals for `language` field (known languages)
                let currentUserLanguage = userSettings.fieldDescriptorSettings
                    .find((field) => field.fieldRef === "language")?.fieldIntervals?.find((interval) =>
                        interval.from === initializationsForClient.currentUser?.language);
                if (currentUserLanguage) {
                    language = initializationsForClient.currentUser.language
                }
            }
        }

        Messages.getInstance().setLanguage(language);

        // Set the values for context after setting language, in order for the other components
        // which depends on context values to use the new language on rerendering.
        await this.helperAppContainer.dispatchers.setInReduxState({
            appContainerContextValue: {
                ...this.helperAppContainer.dispatchers.getState().appContainerContextValue,
                initializationsForClient: initializationsForClient
            }
        });

        // We will add entries in menu only if a user is logged in 
        if (initializationsForClient.currentUser) {
            if (this.hasHomePageMenuEntry()) {
                this.menuEntries.push({ content: _msg("HomePage.title"), to: "/", icon: "home", exact: true });
            }

            // We update the content of the menu entries in order for their messages to be translated in the current user language
            this.entityCrudMenus = this.entityCrudMenus!.map(menuEntry => { return { ...menuEntry, content: _msg({ pluralized: true }, menuEntry.entityMenuProps.labelKey) } });
            this.entityCrudMenus = this.entityCrudMenus!.sort(this.compareMenuEntries);
            this.menuEntries.push(...this.createMenuEntries(initializationsForClient, this.entityCrudMenus));

            if (initializationsForClient.currentUser?.isAdmin || AppMetaTempGlobals.appMetaInstance.hasPermission(DATABASE_MENU_ENTRY, false, initializationsForClient.currentPermissions)) {
                this.menuEntries.push({ content: _msg("Menu.database"), icon: "database", submenus: this.entityCrudMenus });
            }
            this.updateResponseStateMenuEntry();
        }

        if (initializationsForClient.currentUser) {
            this.initRRCSingletons();
            await this.loadOrganizations();
        }

        this.helperAppContainer.dispatchers.setInReduxState({
            initializationsForClientLoaded: true
        });

        // assign/switch "/" for routes: home <-> personalHomePage
        for (let i = 0; i < this.routes.length; i++) {
            const route = this.routes[i];
            let props: AppRouteProps = route.props;
            if (props.homePageType) {
                let newPath: string;
                if (props.homePageType === "normal") {
                    newPath = initializationsForClient.homePageSettings.usePersonalHomePage ? "/homeOld" : "/";
                } else {
                    // props.homePageType === "personal"
                    newPath = initializationsForClient.homePageSettings.usePersonalHomePage ? "/" : "/personalHomePage";
                }
                this.routes[i] = <route.type key={route.key} {...props} path={newPath} />
            }
        }
        if (AppMetaTempGlobals.history.location.pathname === "/") {
            // if pers home page, sometimes the normal home page is still displayed
            // a re-render (e.g. via "forceUpdate()") would suffice; but currently I cannot have a reliable
            // comp here; hence I work on the history; I observed that this action doesn't add a new 
            // history entry, and it seems to force a rerender
            AppMetaTempGlobals.history.push("/");
        }

        window.sessionStorage.removeItem(sessionGlobalReload);

        return initializationsForClient;
    }

    updateResponseStateMenuEntry() {
        const forcedMode = this.helperAppContainer.dispatchers.getState().appContainerContextValue.responsiveState.forcedMode;
        const currentResponsiveMode = AppMetaTempGlobals.appMetaInstance.getCurrentResponsiveMode(AppMetaTempGlobals.appMetaInstance.responsiveMatchMedia());
        const menuEntryIndex = AppMetaTempGlobals.appMetaInstance.menuEntries.findIndex(entry => entry.key === RESPONSIVE_STATE_FORCED_MODE_KEY);
        const menuEntryTitle = forcedMode === ResponsiveMode.DESKTOP ? "Desktop mode forced (Switch to Mobile)" : "Mobile mode (Switch to Desktop)";
        if (currentResponsiveMode === ResponsiveMode.MOBILE) {
            if (menuEntryIndex !== -1) {
                AppMetaTempGlobals.appMetaInstance.menuEntries[menuEntryIndex].content = menuEntryTitle;
            } else {
                AppMetaTempGlobals.appMetaInstance.menuEntries.push({
                    key: RESPONSIVE_STATE_FORCED_MODE_KEY,
                    content: menuEntryTitle,
                    as: "div",
                    onClick: () => {
                        const forcedMode = this.helperAppContainer.dispatchers.getState().appContainerContextValue.responsiveState.forcedMode;
                        AppMetaTempGlobals.appMetaInstance.updateResponsiveStateMode(forcedMode === ResponsiveMode.DESKTOP ? undefined : ResponsiveMode.DESKTOP);
                    }
                });
            }
        } else {
            menuEntryIndex !== -1 && AppMetaTempGlobals.appMetaInstance.menuEntries.splice(menuEntryIndex, 1);
        }
    }

    updateResponsiveStateMode(newForcedMode: ResponsiveMode | undefined) {
        this.helperAppContainer.dispatchers.updateResponsiveState(
            { mode: newForcedMode === undefined ? this.getCurrentResponsiveMode(this.responsiveMatchMedia()) : newForcedMode, forcedMode: newForcedMode });
    }

    /**
     * Copies (merges) the FieldDescriptorSettings received from the server, into the ones that exist on the client.
     * Having FDS defined on the client is not a common practice, but it's possible.
     */
    protected populateDescriptorsWithCrudSettings(crudSettings?: CrudSettings) {
        resetToEntityDescriptorsWithoutCrudSettingsOverride();
        crudSettings?.forEntities.map(forEntity => {
            const ed = entityDescriptors[forEntity.entityName];
            if (!ed) {
                return;
            }
            ed.entityDescriptorSettings = forEntity;
            forEntity.fieldDescriptorSettings.forEach(fdSettings => {
                if (!fdSettings.fieldRef || ed?.getFieldDescriptorChain(fdSettings.fieldRef).length === 0 || ed?.getFieldDescriptorChain(fdSettings.fieldRef)[0] === undefined) {
                    return;
                }
                let fds = [ed?.getField(fdSettings.fieldRef)];
                fds.forEach(fd => {
                    if (fd) {
                        // IMPORTANT: for lists it will merge items based on their indexes!!! 
                        // For the moment we don't see a real case for it.
                        // There is also a 'mergeWith' if we need to do something else for them in the future.
                        fd.fieldDescriptorSettings = lodash.merge(fd.fieldDescriptorSettings, fdSettings);
                        if (fdSettings.icon) {
                            fd.icon = fdSettings.icon as SemanticICONS;
                        }
                    }
                });
            })
        });
        // Because we store descriptors also in uiEntityDescriptors (copied from entityDescriptors), we need also to update them in
        // order to have the updated settings. We had in past problems in table on getting the CC based on settings. In table we would have
        // a descriptor without settings (because these ui descriptors are populated on the beginning of rendering page), despite there was
        // some settings with a default CC and the descriptor from entityDescriptors would be updated correctly.
        this.initializeUIEntityDescriptors();
    }

    public hasPermission(permission: string, showErrorMessageIfNoPermission?: boolean, permissions?: any): boolean {
        if (TestUtils.storybookMode || Utils.isTest()) {
            return true;
        }
        const value = this.helperAppContainer.dispatchers.hasPermission(permission, permissions);
        if (!value && showErrorMessageIfNoPermission) {
            permission = `<b>${getPermissionLabel(permission, true)}</b> (${permission})`;
            this.helperAppContainer.dispatchers.showGlobalAlert({ message: _msg("error.insufficient.rights.title") + "\n" + _msg("error.insufficient.rights.details", permission), severity: Severity.ERROR });
        }
        return value;
    }

    protected async getEntityDescriptorsForServer() {
        const operationName = "entityDescriptorForServerService_entityDescriptors";
        const result = (await apolloClientHolder.apolloClient.query({ query: gql(`query q{ ${operationName} }`), context: { showSpinner: false } })).data[operationName];
        Object.assign(EntityDescriptorForServerUtils.entityDescriptorsForServer, result);
    }

    protected addCustomFieldsToEntityDescriptors() {
        for (const ed of Object.values(entityDescriptors)) {
            const fields = Object.values(ed.fields).filter(field => field.isCustomField === true).map(field => field.name);
            if (fields && fields.length > 0) {
                ed.removeFieldDescriptors(...fields);
            }
        }

        new Set<string>(Object.values(EntityDescriptorForServerUtils.entityDescriptorsForServer)
            .map(entity => entity.entityName))
            .forEach(entityName => {
                Object.entries(EntityDescriptorForServerUtils.entityDescriptorsForServer[entityName].fields)
                    .filter(([key, field]) => key === field.fieldName && field.customField)
                    .forEach(([key, field]) => {
                        entityDescriptors[entityName].addFieldDescriptor({
                            name: field.fieldName,
                            type: field.fieldType || FieldType.defaultScalar,
                            isCustomField: true
                        })
                    })
            });
    }

    protected getFieldsForOrg() {
        return "id name qualifiedName"
    }

    protected getStructureForOrg() {
        const orgFields = this.getFieldsForOrg();
        return `${orgFields} parent { ${orgFields} } children { 
            ${orgFields} children {
                ${orgFields} children {
                    ${orgFields} children {
                        ${orgFields} 
                    }
                }
            }
        }`;
    }

    protected configQueries = () => {
        const orgStructure = this.getStructureForOrg();
        this.loadOrganizationsQuery = gql(`query userService_getOrganizationsForCurrentUser {
            userService_organizationsForCurrentUser { 
                ${orgStructure}
            } 
          }`);
    }

    protected async loadOrganizations() {
        const appContainerContextValue = this.helperAppContainer.dispatchers.getState().appContainerContextValue;
        if (appContainerContextValue?.organizations) {
            return;
        }

        const result: [] = (await apolloClientHolder.apolloClient.query({ query: this.loadOrganizationsQuery, context: { showSpinner: false } })).data.userService_organizationsForCurrentUser;
        this.helperAppContainer.dispatchers.setInReduxState({ appContainerContextValue: { ...appContainerContextValue, organizations: result }, root: this.getRootForOrganizations(result) });
        this.helperAppContainer.dispatchers.setInReduxState({ leftBarRoot: this.getLeftBarOrganizationRoot(appContainerContextValue.initializationsForClient.currentOrganizationDropdownValue, result) });
    }

    getLeftBarOrganizations(organizationDropdownValue: Optional<OrganizationDropdownValue>, organizations?: Organization[]): Organization[] {
        if (!getFilterLeftBarCheck()) {
            return organizations || [];
        }
        const filterLeftBarOrganizations = getFilterLeftBarOrganizations();
        const currentOrganization = organizationDropdownValue?.organization;
        const organizationsFiltered = organizations?.filter((organization: Organization) => filterLeftBarOrganizations.includes(organization.qualifiedName) || (currentOrganization && currentOrganization == organization.qualifiedName));
        return organizationsFiltered || [];
    }

    getLeftBarOrganizationRoot(organizationDropdownValue: Optional<OrganizationDropdownValue>, organizations?: Organization[]): OrganizationTreeRoot {
        if (!getFilterLeftBarCheck()) {
            return this.helperAppContainer.dispatchers.getState().root;
        }
        return AppMetaTempGlobals.appMetaInstance.getRootForOrganizations(this.getLeftBarOrganizations(organizationDropdownValue, organizations));
    }

    getRootForOrganizations(organizations: Organization[]): OrganizationTreeRoot {
        const appContainerContextValue = this.helperAppContainer.dispatchers.getState().appContainerContextValue;
        let children: Organization[] = [];

        if (appContainerContextValue.initializationsForClient.allOrganizationsAccess) {
            children.push({ id: NO_ORGANIZATION.key, name: NO_ORGANIZATION.text, qualifiedName: NO_ORGANIZATION.text });
        }
        organizations
            // include all orgs with a null parent and those whose parent is not in the list (e.g. the user does not have access)
            .filter((x: Organization) => x.parent === null || !organizations.find((o: Organization) => o.qualifiedName === x.parent?.qualifiedName))
            .forEach((x: Organization) => children.push(x));
        return { children: children };
    }

    protected getClientInitializationService_initializationsForClient_query(fields: string) {
        return gql(`query q { clientInitializationService_initializationsForClient { ${fields} } }`);
    }

    protected getSecurityFieldsForInitializationsForClient() {
        const orgStructure = this.getStructureForOrg();
        return ` currentRole {id name} currentOrganization { ${orgStructure} } currentOrganizationDropdownValue { organization includeMyOtherOrganizations alsoIncludeOrganizations } currentPermissions allOrganizationsAccess ldapAvailable rememberMeEnabled`;
    }

    protected getFieldsForInitializationsForClient() {
        let result = "";
        if (ConnectedPageHelper.tempEnableSecurity) {
            result += this.getSecurityFieldsForInitializationsForClient();
        }

        result += ` version 
        visualStyleSettings { appTitle headerLogo clientHeaderLogo whiteBackgroundHeaderLogo whiteBackgroundClientHeaderLogo homeLogo homeTitle homeBackgroundImage homeLogo2 headerLogoRight } 
        homePageSettings { usePersonalHomePage footerBarText homePageDashboards { id } defaultPage defaultPageMobile }
        internationalizationSettings { defaultLanguage showBothOriginalAndTranslated translatableByUserLanguagesMap dateFormat timeFormat dateFormats { name momentJsFormat momentJsFormatShorter } timeFormats { name momentJsFormat momentJsFormatShorter }  }
        colorSettings { data { name value thisColorIsAliasFor } } 
        notificationSettings { maxNumberOfNotificationDisplayed refreshRate }
        auditSettings { maxRowsForPopup }
        userSettings { passwordMinLength passwordMaxLength  passwordHasUpperCaseConstraint passwordHasLowerCaseConstraint passwordHasNumberConstraint passwordHasSpecialCharacterConstraint }
        crudSettings { forEntities { entityName defaultColumnConfig defaultCustomQuery externalLink editorHeaderImage fieldsInHeader { name fields } fieldDescriptorSettings { fieldRef inHeaderOrderIndex physicalQuantity measurementUnit numberOfDecimals fieldIntervals { from to enumOrderIndex color applyColorTo label } } } physicalQuantities { name defaultMeasurementUnitSymbol measurementUnits { name symbol scaleFactorTowardsDefaultMeasurementUnit translationFactorTowardsDefaultMeasurementUnit } } }
        organizationSettings { globalOrganizationFilter }
        oktaAuthenticationAvailable
        fileBrowserBulkDownloadMaximumFileSize
        tempSettings { historyGraphInitialFields }
        `;

        return result;
    }

    beforeLogout() {
        // nothing here, used by super
    }

    createLogoutParams() {
        return { oktaUserId: document.cookie.split('; ').find((row: string) => row.startsWith('oktaUserId='))?.split('=')[1] };
    }

    async logout() {
        this.beforeLogout();

        // We need to clean sessionStorage at logout. In this way, different unsaved changes from this session
        // (e.g. changes for default CCs or CQs on different tables) are deleted and default values are restored
        window.sessionStorage.clear();

        // don't keep the forcedMode
        AppMetaTempGlobals.appMetaInstance.updateResponsiveStateMode(undefined);

        this.helperAppContainer.dispatchers.setInReduxState({
            appContainerContextValue: {
                ... this.helperAppContainer.dispatchers.getState().appContainerContextValue,
                // @ts-ignore
                initializationsForClient: undefined,
                organizations: undefined
            }, initializationsForClientLoaded: false, root: undefined
        });

        // when processing a GraphQL request server-side, we don't have a access to a HTTP response
        // to delete a cookie for the client. Server-side, we lose the token entity for this session, so,
        // even if we didn't delete it here, automatic login would not be possible.
        document.cookie = "auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

        await apolloClient.mutate<logout>({ mutation: LOGOUT, variables: { logoutParams: this.createLogoutParams() } });

        await this.loadInitializationsForClient();

        if (ConnectedPageHelper.tempEnableSecurity) {
            AppMetaTempGlobals.history.push("/login");
        }
    }

    async afterLogin() {
        await AppMetaTempGlobals.appMetaInstance.loadInitializationsForClient();
    }

    getAppTitle(visualStyleSettings: Optional<VisualStyleSettings>) {
        return visualStyleSettings?.appTitle || "App title";
    }

    public getAdditionalItemsForRightMenu(): any | null {
        return null;
    }

    public getAdditionalUserMenuEntries(): any[] {
        return [];
    }

    public getAdditionalUserHeaderEntries(): any[] {
        return [];
    }

    public getIsSpecialPageWithoutHeader() {
        return AppMetaTempGlobals.history.location.pathname.startsWith("/passwordResetFinish");
    }

    public goBack() {
        AppMetaTempGlobals.history.goBack();
    }

    public reload() {
        window.sessionStorage.setItem(sessionGlobalReload, "true");
        window.location.reload();
    }

    addEntityDescriptors(eds: EntityDescriptor[]) {
        // nop for the moment; at least forces the user to import the script and call this
        // in the future, this should invoke a runnable to trigger the customization
    }

    finalizeEntityDescriptors() {
        // duplicated below :(
        EntityDescriptorPopulatorFromEntityGen.INSTANCE.processRemainingEntitiesGen();
        runAfterStartupRunnables();

        // create pages and add in menu list only the ones in uiEntityDescriptors
        let eds = this.getUIEntityDescriptors();
        const entityCrudMenus = [];
        for (let ed in eds) {
            const d = eds[ed];
            this.addConnectedPageInfos(d.getHelpers());
            entityCrudMenus.push(d.getMenuEntry());
        }

        return entityCrudMenus;
    }

    initializeUIEntityDescriptors() {
        this.uiEntityDescriptors = {};
        Object.values(entityDescriptors).filter(ed => ed.showInUI).map(ed => this.uiEntityDescriptors![ed.name] = ed);
    }

    getUIEntityDescriptors(): { [entityName: string]: EntityDescriptor } {
        if (this.uiEntityDescriptors === undefined) {
            this.initializeUIEntityDescriptors();
        }
        return this.uiEntityDescriptors!;
    }

    protected getSessionStorageResponsiveStateForcedModeItem(): ResponsiveMode | undefined {
        const rawValue = sessionStorage.getItem(RESPONSIVE_STATE_FORCED_MODE_KEY);
        if (rawValue === null) {
            return undefined;
        }
        return parseInt(rawValue) as ResponsiveMode;
    }

    /**
     * If multiple modes are required, the method should be extended  to include the new statuses.
     */
    responsiveSetup() {
        const query = this.responsiveMatchMedia();
        // init state
        this.updateResponsiveStateMode(this.getSessionStorageResponsiveStateForcedModeItem());

        query.addEventListener('change', (event) => this.onResponsiveMediaChange(event));
    }

    protected getCurrentResponsiveMode(query: MediaQueryList | MediaQueryListEvent) {
        return query.matches ? ResponsiveMode.DESKTOP : ResponsiveMode.MOBILE;
    }

    protected responsiveMatchMedia(): MediaQueryList {
        return matchMedia('(min-width: 768px)');
    }

    protected onResponsiveMediaChange(event: MediaQueryListEvent) {
        const forcedMode = this.helperAppContainer.dispatchers.getState().appContainerContextValue.responsiveState.forcedMode;
        this.updateResponsiveStateMode(this.getCurrentResponsiveMode(event) === forcedMode ? undefined : forcedMode);
    }

    protected renderRRCSingletons() {
        return <></>;
    }

    getAppComponent = () => {
        if (this.componentCreated) {
            throw new Error("Illegal state; app already created");
        }

        if (TestUtils.storybookMode) {
            // duplicated from above :(
            runAfterStartupRunnables();
        }
        initApolloClient();

        const that = this;
        const history = createHashHistory();
        AppMetaTempGlobals.history = history;
        history.listen(l => {
            AppMetaTempGlobals.locationPathnamePrevious = AppMetaTempGlobals.locationPathnameCurrent;
            AppMetaTempGlobals.locationPathnameCurrent = l.pathname;
        });

        return class extends React.Component<any, { reloading: boolean }> {
            constructor(props: any) {
                super(props);
                this.softReload = this.softReload.bind(this);

                that.store = that.createStore(undefined, history);

                this.state = { reloading: false };
                that.loadInitializationsForClient_fromConstructor();
            }

            /**
             * Previously, when the user would change the organization, a full web page reload would be triggered. This was reasonable,
             * because we want all state cleared, which may be: Redux, local component state, "normal" variables/attributes. We don't want
             * an individual component or page to have an additional responsibility: "please be ready to do a full reset, if the user changes
             * the org".
             * 
             * However, this is a no go when executing tests. Because the test is run within the window of this app which is just being reloaded.
             * 
             * So we introduced this function. The process is the following:
             * 1. `this.state.reloading = true`;
             * 2. on next update/render the root component will unmount everything. So all components in the hierarchy are unmounted / die. Normally
             * this would kill all kind of internal state
             * 3. on next update: schedule that on next update (when the components are unmounted), a "store empty" action is issued
             * 4. the components will render again normally, and the Redux state will begin naturally to be populated by the components that
             * are just mounted
             */
            softReload() {
                this.setState({ reloading: true });
            }

            async componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<{ reloading: boolean; }>): Promise<void> {
                if (prevState.reloading !== this.state.reloading && this.state.reloading) {
                    // needed because the components aren't yet unmounted; see ConnectedPageHelper.initHOCs() -> componentWillUnmount()
                    await Utils.setTimeoutAsync();

                    // Removing the items in session storage so that for an org switch => default org CC/CQ are used instead of what the user had in storage
                    // TODO: this should be replaced with a subscription mechanism
                    window.sessionStorage.removeItem(currentColumnConfigId);
                    window.sessionStorage.removeItem(currentEditorColumnConfigId);
                    window.sessionStorage.removeItem(currentCustomQueryId);
                    // rerender & reload data
                    this.setState({ reloading: false });
                    await that.loadInitializationsForClient();
                }
            }

            render() {
                return <>
                    <Provider store={that.store}>
                        {that.renderRRCSingletons()}
                        <ColorRegistryRRC id={RRC_SINGLETON} />
                        <RootReducerForPages.Context.Provider value={that.newRootReducer}>
                            <ZeroTrainingContext.Provider value={that.zeroTrainingArticlesRegistry}>
                                <ConnectedRouter history={history}>
                                    <that.helperAppContainer.componentClass onSoftReload={this.softReload} />
                                </ConnectedRouter>
                            </ZeroTrainingContext.Provider>
                        </RootReducerForPages.Context.Provider>
                    </Provider>
                </>;
            }
        }
    }

    protected hasHomePageMenuEntry() {
        return true;
    }

    protected createMenuEntries(data: FoundationInitializationsForClient, entityCrudMenus: any): any {
        return [];
    }

    protected resetMenuEntries() {
        this.menuEntries = [];
    }

    protected compareMenuEntries(a: any, b: any) {
        return a.content.localeCompare(b.content);
    }

    enableModalRoutes() {
        return true;
    }

    getColor(colorKey: any): string {
        const colorFromRegistry = ColorRegistry.INSTANCE?.get(colorKey);
        if (colorFromRegistry && colorFromRegistry !== -1 && colorFromRegistry !== 0) {
            return Utils.convertColorToHex(colorFromRegistry);
        }
        if (typeof colorKey === 'number') {
            return Utils.convertColorToHex(colorKey);
        }
        if (typeof colorKey === 'string' && !colorKey.trim().startsWith("#")) {
            return Utils.DEFAULT_TEXT_COLOR;
        }
        return colorKey;
    }

    getOrganizationTreeIcon(itemId: string, object: any, currentOrg: Optional<Organization>, iconProps?: any, getDefaultOrgIconForParents?: boolean) {
        let icon = organizationEntityDescriptor.getIcon(iconProps);
        if (object.children?.length > 0 && (itemId !== NO_ORGANIZATION.key) && !getDefaultOrgIconForParents) {
            icon = <></>;
        }
        return icon;
    }

    getUserInfo(user: User): React.ReactNode {
        return <div className="flex-container gap5 AppContainer_userInfo">
            <span className="no-wrap-no-overflow-ellipsis"><Icon name='user' className="AppContainer_menu_icon_without_label" /> {user?.username}</span>
            {this.renderAdditionalUserInfo(user)}
        </div>
    }

    getVersionInfo(version: string): React.ReactNode {
        return <span>{version}</span>;
    }

    protected renderAdditionalUserInfo(user: User): React.ReactNode {
        return <></>;
    }
}

class SliceAppContainer {
    initialState = {
        drawerOpen: false,
        searchBarHasFocus: false,
        initializationsForClientLoaded: false,
        avoidDisplayingInitializationsLoadingSpinner: false,

        globalAlertTitle: undefined as string | undefined,
        globalAlertMessage: undefined as string | undefined,
        globalAlertShownAsMessage: false,
        menuEntries: {} as { [key: string]: Optional<MenuEntry[]> },
        globalAlertSeverity: undefined as unknown as Severity | undefined,

        // support for modal routes; if enabled, these are calculated by onLocationChanged(); and then these locations are used 
        // during render; i.e. from here/Redux state, instead of the current location of the browser
        locationForDrawer: undefined as Location<any> | undefined,
        locationsForModals: [] as Location<any>[],
        currentUserModalOpen: false,
        showSpinner: false,
        root: { children: [] } as OrganizationTreeRoot,
        organizationSelectedId: undefined as string | undefined,
        spinnerText: undefined as string | undefined,
        showSpinnerCloseButton: true,
        spinnerCloseConfirmationModalOpen: false,
        appContainerContextValue: undefined as unknown as AppContainerContextValue,
        organizationSelectorPopupOpen: false,
        filterLeftBarCheck: getFilterLeftBarCheck(),
        combineCheck: getCombineCheck(),
        leftBarRoot: { children: [] } as OrganizationTreeRoot,
    }

    nestedSlices = {
        notificationComponent: sliceNotificationComponent
    }

    reducers = {
        ...getBaseReducers<SliceAppContainer>(this),

        // message = undefined => closes the modal, everything else shows the modal with the message
        // DON'T use directly; use Utils.showGlobalAlert() instead
        showGlobalAlert(state: StateFrom<SliceAppContainer>, params: ShowGlobalAlertParams) {
            state.globalAlertTitle = params.title;
            state.globalAlertShownAsMessage = false;
            state.globalAlertMessage = params.message;
            state.globalAlertSeverity = params.severity;
        },

        updateResponsiveState(state: StateFrom<SliceAppContainer>, value: Partial<ResponsiveState>) {
            state.appContainerContextValue = {
                ...state.appContainerContextValue,
                responsiveState: {
                    ...state.appContainerContextValue?.responsiveState,
                    ...value
                }
            };
        }
    }

    impures = {
        ...getBaseImpures<SliceAppContainer>(this),

        hasPermission(permission: string, permissions?: any): boolean {
            return Utils.hasPermission(permission, permissions ? permissions : this.getState().appContainerContextValue.initializationsForClient.currentPermissions);
        },

        async loadMenuEntries(entityName: string) { // can be used only for Dashboard or Chart (note: Chart isn't in foundation yet)
            const operationName = _.lowerFirst(entityName) + "Service_findByFilter";
            const params = FindByFilterParams.create().filter(Filter.create("showInMenu", FilterOperators.forBoolean.equals, "TRUE")).sorts([{ field: "positionInMenu", direction: "ASC" }]);
            const query = gql(`query q($params: FindByFilterParamsInput) { 
                ${operationName}(params: $params) {
                    results {
                        id, name, icon, color
                    }
                }
            }`);
            const menuEntries = (await apolloClient.query({ query: query, variables: params, context: { showSpinner: false } })).data[operationName].results;
            this.getDispatchers().setInReduxState({ menuEntries: { ...this.getState().menuEntries, ...{ [entityName]: menuEntries } } });
        }
    }
};

export const sliceAppContainer = createSliceFoundation(SliceAppContainer)

export type AppContainerProps = PropsFrom<SliceAppContainer> & { onSoftReload: () => void;[key: string]: any };

export class AppContainer extends React.Component<AppContainerProps> {

    protected refTreeMenu = React.createRef<TreeMenu>();
    protected refTreeMenuSearchWrapper = React.createRef<WrapperWithRenderFunction>();
    protected onLocationChangedUnregisterCallback: UnregisterCallback;
    protected refOrganizationTree = React.createRef<OrganizationTree>();
    protected refOrganizationSelector = React.createRef<OrganizationSelector>();

    public static INSTANCE: AppContainer;

    constructor(props: AppContainerProps) {
        super(props);
        if (AppContainer.INSTANCE) {
            throw new Error("Illegal state: an instance of AppContainer already exists!");
        }
        AppContainer.INSTANCE = this;

        // commented because the FBA can get the option from settings and here it isn't yet loaded
        // w/o the if some additional changes will be seen in state, but they aren't used because of the other if in render()
        // if (AppMetaTempGlobals.appMetaInstance.enableModalRoutes()) {
        this.onLocationChanged = this.onLocationChanged.bind(this);
        // CS: I tried w/ props.location; I saw this field working in other places; but here not; for lack of time I turned to ...TempGlobals
        if (AppMetaTempGlobals.appMetaInstance.canCallOnLocationChangedFromAppContainerConstructor()) {
            this.onLocationChanged(AppMetaTempGlobals.history.location);
        }
        // we forgot this initially; and we had issues during test, because multiple instances would continue to listen
        this.onLocationChangedUnregisterCallback = AppMetaTempGlobals.history.listen(this.onLocationChanged)
        // }
    }

    /**
     * From React: shouldn't be used!
     * This function is here for very special cases required by legacy parts of apps that are non-React.
     */
    getAppContainerContextValue(): AppContainerContextValue {
        return this.props.appContainerContextValue;
    }

    async setCurrentOrganization(organizationDropdownValue: OrganizationDropdownValue) {
        const mutation = gql(`mutation setOrganization($organizationDropdownValue: OrganizationDropdownValueInput) {
            authService_setOrganization(organizationDropdownValue: $organizationDropdownValue)
        }`);
        await apolloClient.mutate({ mutation: mutation, context: { showSpinner: false }, variables: { organizationDropdownValue } });

        this.props.onSoftReload();
    }

    componentDidMount(): void {
        AppMetaTempGlobals.appMetaInstance.responsiveSetup();
    }

    componentWillUnmount(): void {
        this.onLocationChangedUnregisterCallback();
    }

    componentDidUpdate(prevProps: AppContainerProps) {
        this.componentDidUpdateInternal(prevProps);
    }

    componentDidUpdateInternal(prevProps?: AppContainerProps) {
        if (AppMetaTempGlobals.appMetaInstance.showSearchBarOnTitleBar() && (!prevProps || prevProps.drawerOpen !== this.props.drawerOpen) && !this.props.drawerOpen) {
            this.props.dispatchers.setInReduxState({ searchBarHasFocus: false });
            this.refTreeMenu.current!.modifySearchExpression("");
        }
        if (prevProps?.appContainerContextValue && !_.isEqual(prevProps.appContainerContextValue.responsiveState, this.props.appContainerContextValue.responsiveState)) {
            const forcedMode = this.props.appContainerContextValue.responsiveState.forcedMode;
            if (forcedMode === undefined) {
                sessionStorage.removeItem(RESPONSIVE_STATE_FORCED_MODE_KEY);
            } else {
                sessionStorage.setItem(RESPONSIVE_STATE_FORCED_MODE_KEY, String(forcedMode));
            }
            AppMetaTempGlobals.appMetaInstance.updateResponseStateMenuEntry();
        }
    }

    /**
     * In some cases there is no entityDescriptor for routeEntityName (e.g. CurrentUser).
     * This method can be overwritten when necessary.
     */
    public createLocationFromRouteEntityName(routeEntityName: string) {
        /**
          * Suntem aici pt ca s-a cerut ruta de editor direct in browser. Deci a comandat si "pe fundal"
          * tabelul. Noi tr sa transmitem in state-ul rutei treaba asta. Pentru ca tabelul cand isi face logica
          * de redirect (atat redirectul imediat cat si cel in urma sosirii dashb) sa transmita la randul lui
          * in acea "location" catre care se face redirect o info supl: dontCloseDrawer. Care o vom trata aici. In cazul de fata in 
          * background. Mai precis informatia e "ba, mergi pe ruta asta in slotul de tabel si LASA editorul ON"
          */
        return createLocation(entityDescriptors[routeEntityName].getEntityTableUrl(), { dontCloseDrawer: true });
    }

    protected onLocationChanged(location: Location<any>) {
        const props = this.props;
        let foundRoute: Optional<ReactElement> = undefined;
        for (const child of React.Children.toArray(AppMetaTempGlobals.appMetaInstance.routes)) {
            const route: ReactElement = child as any;
            if (!route.props.path) {
                throw new Error("Current element doesn't seem to be a <Route .../>: " + Utils.consoleLogJson(route));
            }
            if (matchPath(location.pathname, route.props)) {
                foundRoute = route;
                break;
            }
        }
        if (foundRoute?.props["routeIsModal"]) {
            if (foundRoute.props["routeEntityName"]
                // e.g. A) displaying as main the table page for employees; and I want to display the editor for THAT entity type 
                && (foundRoute.props["routeEntityName"] === EntityTablePage.tempEntityForCurrentTablePage)) {
                // => show it as drawer
                props.dispatchers.setInReduxState({ locationForDrawer: location, locationsForModals: [] /* cancel any other modals, if they exist */ });
            } else {
                // a normal (non-editor) modal, or a editor modal and: 1) main is not a table page or 2) is a table page, but for another entity
                // so add a new modal in the stack

                // DISCUSSION: the feature was intended to allow a stack of modals; however w/o a lot of effort and checks, this is the case "better is the enemy of good"
                // #1 Conceptually. Should we check to see if route.pathname of last modal == crt one? However, in practice, is difficult to reproduce. Should have a link from own editor in editor.
                // #2 The above case has a more pragmatic side effect: 2 slices w/ same name. Possible solution: per route in stack, specify the entityName. BUT we may also
                // have the case of normal modal; w/o entity, but w/ a connected component. In this case => same error
                // SO for the moment, the stacking is disabled, and we allow only one modal
                props.dispatchers.setInReduxState({ locationsForModals: [/*...props.dispatchers.getState().locationsForModals,*/ location] });
            }
        } else {
            // dontCloseDrawer = true => entered on our site directly w/ an editor URL, 
            // we need to keep main open on background BUT also display the drawer + the route must point to the drawer location
            const dontCloseDrower = location.state?.dontCloseDrawer && props.dispatchers.getState().locationForDrawer;
            props.dispatchers.setInReduxState({
                locationForDrawer: dontCloseDrower ? props.dispatchers.getState().locationForDrawer : undefined,
                locationsForModals: []
            });
            if (dontCloseDrower) {
                props.dispatchers.dispatch(push({ pathname: props.dispatchers.getState().locationForDrawer!.pathname, state: props.dispatchers.getState().locationForDrawer?.state }));
            }
        }
    }

    /**
     * Meant to be overriden
     */
    renderAdditionalContent(): JSX.Element | null {
        return null;
    }

    protected getHeaderClassName() {
        return "";
    }

    renderLocationsModals(props: any) {
        return <>{props.locationsForModals.map((location: any) => (<ModalExt open={true} closeOnDimmerClick={true} closeIcon={true}
            onClose={() => {
                (!props.locationForDrawer && AppMetaTempGlobals.appMetaInstance.tempLocationForEditorBackground) && props.dispatchers.dispatch(push(AppMetaTempGlobals.appMetaInstance.tempLocationForEditorBackground.pathname));
                props.dispatchers.setInReduxState({ locationsForModals: props.locationsForModals.slice(0, -1) });
            }}>
            <Modal.Content className="AppModal_content">
                <Switch location={location}>
                    {AppMetaTempGlobals.appMetaInstance.routes}
                </Switch>
            </Modal.Content>
        </ModalExt>))}</>
    }

    protected renderHeaderLogo(settings: VisualStyleSettings) {
        let headerLogoStyle = settings.whiteBackgroundHeaderLogo ? " HomePage_headerLogo_whiteBackground HomePage_headerLogo_image" : "";
        return AppMetaTempGlobals.appMetaInstance.showHeaderLogoItem()
            ? (settings.headerLogo ?
                (<Menu.Item className={"less-padding" + headerLogoStyle} header as={Link} to="/"><img src={Utils.adjustUrlToServerContext(settings.headerLogo)} alt='headerLogo' /></Menu.Item>) :
                (<Menu.Item header as={Link} className={"less-padding"} to="/"><Header as="h4">{AppMetaTempGlobals.appMetaInstance.getAppTitle(settings)}</Header></Menu.Item>))
            : null;
    }

    protected renderHomeButton() {
        return <Menu.Item icon="home" as={Link} to="/" />;
    }

    render() {
        return <>
            <Dimmer data-testid="AppMeta_dimmer" active={this.props.showSpinner} page={true}><Loader size='large'>
                <Header as="h3" inverted>{this.props.spinnerText ? this.props.spinnerText : _msg("general.loading")}</Header>
                {this.props.showSpinnerCloseButton
                    ? <>
                        <Button data-testid={"spinner-close"} negative size="tiny" icon="delete" onClick={() => this.props.dispatchers.setInReduxState({ spinnerCloseConfirmationModalOpen: true })} />
                        <ModalExt size="mini" open={this.props.spinnerCloseConfirmationModalOpen} closeIcon={true} onClose={() => this.props.dispatchers.setInReduxState({ spinnerCloseConfirmationModalOpen: false })}>
                            <Modal.Content>{_msg("AppHelper.abortGraphQLConfirmation")}</Modal.Content>
                            <Modal.Actions>
                                <Button data-testid={"spinner-abort-yes"} onClick={() => this.props.dispatchers.setInReduxState({ spinnerCloseConfirmationModalOpen: false, showSpinner: false })}>{_msg("general.yes")}</Button>
                                <Button data-testid={"spinner-abort-no"} onClick={() => this.props.dispatchers.setInReduxState({ spinnerCloseConfirmationModalOpen: false })}>{_msg("general.no")}</Button>
                            </Modal.Actions>
                        </ModalExt>
                    </>
                    : null}
            </Loader></Dimmer>
            <AppContainerContext.Provider value={this.props.appContainerContextValue}>
                <ScriptableContainer id={"AppContainer"}>
                    {this.renderMain()}
                </ScriptableContainer>
            </AppContainerContext.Provider>
        </>
    }

    isOrganizationDropdownValueEquals(organizationDropdownValue: Optional<OrganizationDropdownValue>) {
        const currentOrganizationDropdownValue = this.props.appContainerContextValue.initializationsForClient.currentOrganizationDropdownValue;
        return ((Utils.isNullOrEmpty(organizationDropdownValue) && Utils.isNullOrEmpty(currentOrganizationDropdownValue))
            || (!Utils.isNullOrEmpty(organizationDropdownValue) && !Utils.isNullOrEmpty(currentOrganizationDropdownValue) &&
                organizationDropdownValue!.organization == currentOrganizationDropdownValue!.organization &&
                organizationDropdownValue!.includeMyOtherOrganizations == currentOrganizationDropdownValue!.includeMyOtherOrganizations
                && Utils.isNullOrEmpty(_.difference(organizationDropdownValue!.alsoIncludeOrganizations, currentOrganizationDropdownValue!.alsoIncludeOrganizations))
                && Utils.isNullOrEmpty(_.difference(currentOrganizationDropdownValue!.alsoIncludeOrganizations, organizationDropdownValue!.alsoIncludeOrganizations))));
    }

    onOpenOrganizationDropdownPopup() {
        this.props.dispatchers.setInReduxState({ organizationSelectorPopupOpen: true });
    }

    async onChangeOrganizationDropdownValue(organizationDropdownValue: Optional<OrganizationDropdownValue>) {
        const newOrganizationDropdownValue = organizationDropdownValue ? { ...organizationDropdownValue } : undefined;
        // No need to include other organizations if the check is not true
        if (newOrganizationDropdownValue && !newOrganizationDropdownValue?.includeMyOtherOrganizations) {
            newOrganizationDropdownValue!.alsoIncludeOrganizations = [];
        }
        if (!this.isOrganizationDropdownValueEquals(newOrganizationDropdownValue)) {
            await this.setCurrentOrganization(newOrganizationDropdownValue!);
        }
        this.props.dispatchers.setInReduxState({
            organizationSelectorPopupOpen: false, filterLeftBarCheck: getFilterLeftBarCheck(), combineCheck: getCombineCheck(),
            leftBarRoot: AppMetaTempGlobals.appMetaInstance.getLeftBarOrganizationRoot(newOrganizationDropdownValue, this.props.appContainerContextValue.organizations)
        });
    }

    renderMain() {
        let eds = AppMetaTempGlobals.appMetaInstance.getUIEntityDescriptors();
        const { props } = this;
        let alert: Optional<ReactElement> = undefined;
        if (props.globalAlertMessage) {
            if (props.globalAlertShownAsMessage) {
                alert = <Redirect to={{ pathname: '/error', state: { headerMessage: props.globalAlertTitle, errorMessage: props.globalAlertMessage } }} />
            } else {
                alert = <ModalExt severity={props.globalAlertSeverity}
                    open={props.globalAlertMessage !== undefined}
                    onClose={() => props.dispatchers.setInReduxState({ globalAlertMessage: undefined })}>
                    <Modal.Header data-testid="AppMeta_globalAlertHeader"><Interweave content={props.globalAlertTitle ? props.globalAlertTitle : _msg("general.info")} /></Modal.Header>
                    <Modal.Content>
                        <Modal.Description data-testid="AppMeta_globalAlertContent">
                            {/* {props.globalAlertMessage.split("\n").map((item, i) => { return <p key={i}>{item}</p> })} */}
                            <Interweave content={props.globalAlertMessage} />
                        </Modal.Description>
                    </Modal.Content>
                    <Modal.Actions>
                        <Scriptable id="AppMeta_globalAlertOkButton" onClick={() => props.dispatchers.showGlobalAlert({ message: undefined })}>{ scriptable => <Button data-testid="AppMeta_globalAlertOk" onClick={() => scriptable.proxy.onClick()} primary>{_msg("general.ok")}</Button>}</Scriptable>
                    </Modal.Actions>
                </ModalExt>
            }
        }
        if (!props.initializationsForClientLoaded) {
            return (<div className="HomePage" data-testid="AppMeta_spinner">
                {props.avoidDisplayingInitializationsLoadingSpinner ? null :
                    <Container>
                        <Message floating>
                            <Header as="h4" color="grey" textAlign="center" icon>
                                <Icon name='spinner' loading />
                                <Header.Content>{_msg("AppHelper.loadingInitializationsForClient")}</Header.Content>
                            </Header>
                        </Message>
                    </Container>}
                {alert}
                {this.renderAdditionalContent()}
            </div>);
        }

        let left: ReactNode;
        let right: ReactNode;

        const { initializationsForClient, organizations, responsiveState } = props.appContainerContextValue;
        // TODO by CS: ? for prot, which doesn't have visualStyleSettings for the moment; remove when the case will be
        const appTitle = initializationsForClient.visualStyleSettings?.appTitle;

        const user = initializationsForClient.currentUser;
        const organization = initializationsForClient.currentOrganization;
        const specialPageWithoutHeader = AppMetaTempGlobals.appMetaInstance.getIsSpecialPageWithoutHeader();
        if (user && !specialPageWithoutHeader) {
            const settings = initializationsForClient.visualStyleSettings;
            let headerLogo: string | undefined = undefined;
            let headerLogoRight: boolean | undefined = undefined;
            let clientHeaderLogo: string | undefined = undefined;
            let clientHeaderLogoStyle: string | undefined = undefined;
            if (settings) {
                headerLogo = settings.headerLogo;
                headerLogoRight = settings.headerLogoRight;
                clientHeaderLogo = settings.clientHeaderLogo;
                clientHeaderLogoStyle = settings.whiteBackgroundClientHeaderLogo ? " HomePage_headerLogo_whiteBackground HomePage_headerLogo_image" : "";
            }
            left = (<Menu key="left" className="AppHelper_menu">
                {AppMetaTempGlobals.appMetaInstance.showBackButton() ? (<Menu.Item key="back" icon="arrow left" onClick={() => AppMetaTempGlobals.appMetaInstance.goBack()} />) : null}
                {AppMetaTempGlobals.appMetaInstance.showReloadButton() ? (<Menu.Item key="reload" icon="redo" onClick={() => AppMetaTempGlobals.appMetaInstance.reload()} />) : null}
                {!headerLogoRight ? this.renderHeaderLogo(settings) : null}
                {AppMetaTempGlobals.appMetaInstance.showHeaderLogoItem() && clientHeaderLogo ? (<Menu.Item key="clientHeaderLogo" className={"less-padding" + clientHeaderLogoStyle} header as={Link} to="/"><img className="margin-auto" src={Utils.adjustUrlToServerContext(clientHeaderLogo)} alt='clientHeaderLogo' /></Menu.Item>) : null}
                {/* If the logo is on the left, show home button first. Else, show menu button first.  */}
                {AppMetaTempGlobals.appMetaInstance.showHomeButton() && !headerLogoRight ? this.renderHomeButton() : null}
                <Menu.Item key="menu" icon="grid layout" as="a" onClick={() => this.props.dispatchers.setInReduxState({ drawerOpen: true })} />
                {AppMetaTempGlobals.appMetaInstance.showHomeButton() && headerLogoRight ? this.renderHomeButton() : null}
            </Menu>);

            const myAccountMenuEntry: any[] = [];
            if (AppMetaTempGlobals.appMetaInstance.showMyAccountMenuEntry) {
                myAccountMenuEntry.push({ key: 'myAccount', text: _msg("login.myAccount"), icon: 'user', onClick: () => this.props.dispatchers.setInReduxState({ currentUserModalOpen: true }) });
            }
            right = (<Menu key="right" className="AppHelper_menu">
                {AppMetaTempGlobals.appMetaInstance.showClockItem() ? <span className='item'><Clock format={Utils.timeWithSecFormat} /></span> : null}
                {AppMetaTempGlobals.appMetaInstance.showTimeZoneItem() ?
                    <span className='item'>
                        <span data-tooltip={moment.tz.guess()} data-position="left center">{"GMT" + (Utils.LOCAL_TIMEZONE_OFFSET === 0 ? "" : (Utils.LOCAL_TIMEZONE_OFFSET > 0 ? "+" : "") + Utils.LOCAL_TIMEZONE_OFFSET.toString())}</span>
                    </span>
                    : null}
                {AppMetaTempGlobals.appMetaInstance.showNotificationsItem() ?
                    <NotificationComponent {...props.notificationComponent} dispatchers={props.dispatchers.notificationComponent} />
                    : null}
                {organizations && AppMetaTempGlobals.appMetaInstance.showOrganizationDropdownItem() ?
                    this.renderOrganizationDropdown()
                    : null}
                <Dropdown pointing="top right" className='link item less-padding' trigger={AppMetaTempGlobals.appMetaInstance.getUserInfo(user)} options={[
                    {
                        key: 'user',
                        text: (
                            <span>
                                {_msg("login.signedInAs", user?.firstName || user?.lastName ? user?.lastName + " " + user?.firstName : user?.username)}
                            </span>
                        ),
                        disabled: true,
                    },
                    ...AppMetaTempGlobals.appMetaInstance.getAdditionalUserHeaderEntries(),
                    ...myAccountMenuEntry,
                    ...AppMetaTempGlobals.appMetaInstance.getAdditionalUserMenuEntries(),
                    { key: 'log-out', text: _msg("login.logout"), icon: 'log out', onClick: e => AppMetaTempGlobals.appMetaInstance.logout() }
                ]}
                />
                {AppMetaTempGlobals.appMetaInstance.getAdditionalItemsForRightMenu()}
                {headerLogoRight ? this.renderHeaderLogo(settings) : null}
            </Menu>);
        }
        const headerClassName = this.getHeaderClassName();
        const locationForMainContainer = (props.locationForDrawer || props.locationsForModals.length > 0) ? AppMetaTempGlobals.appMetaInstance.tempLocationForEditorBackground : AppMetaTempGlobals.history.location;
        return (<><RoutedModals>
            <ContainerWithHeader childTitleAreaLeft={left} childTitleAreaRight={right} titleAreaClassName={AppMetaTempGlobals.appMetaInstance.showSearchBarOnTitleBar() ? "flex-justify-content-center" : undefined}
                displayChildrenInRow={initializationsForClient.currentUser && initializationsForClient.organizationSettings?.globalOrganizationFilter === OrganizationFilter.LEFT_SIDE}
                headerClassName={headerClassName}>
                {AppMetaTempGlobals.appMetaInstance.showHorizontalMenu && user && !specialPageWithoutHeader ?
                    <ContainerWithHeaderContext.Consumer>
                        {value => {
                            if (value.titleAreaDiv) {
                                value.titleAreaDivAlreadyFilledIn = true;
                                return ReactDOM.createPortal(React.createElement(HorizontalMenu, {
                                    menuEntries: AppMetaTempGlobals.appMetaInstance.menuEntries
                                }), value.titleAreaDiv);
                            }
                            else { return null; }
                        }}
                    </ContainerWithHeaderContext.Consumer> : null}
                {AppMetaTempGlobals.appMetaInstance.showSearchBarOnTitleBar() && user && !specialPageWithoutHeader ?
                    <ContainerWithHeaderContext.Consumer>
                        {value => {
                            if (value.titleAreaDiv) {
                                value.titleAreaDivAlreadyFilledIn = true;
                                return ReactDOM.createPortal(<WrapperWithRenderFunction ref={this.refTreeMenuSearchWrapper} render={() =>
                                    <TreeMenuSearch value={this.refTreeMenu.current?.getSearchExpression()}
                                        style={{ maxWidth: '350px', width: '100%' }} focus={this.props.searchBarHasFocus}
                                        onChange={(value: any) => this.refTreeMenu.current?.modifySearchExpression(value)}
                                        onFocus={(value: any) => this.props.dispatchers.setInReduxState({ searchBarHasFocus: value, drawerOpen: value })}
                                    />} />, value.titleAreaDiv);
                            }
                            else { return null; }
                        }}
                    </ContainerWithHeaderContext.Consumer> : null}
                <Drawer destroyOnClose className="AppDrawer"
                    title={<div className="flex-container">{appTitle}{AppMetaTempGlobals.appMetaInstance.getVersionInfo(initializationsForClient.version)}</div>}
                    placement="left" visible={this.props.drawerOpen} mask={!this.props.searchBarHasFocus}
                    onClose={() => this.props.dispatchers.setInReduxState({ drawerOpen: false })}
                    afterVisibleChange={visible => !visible}>
                    {responsiveState.mode !== ResponsiveMode.DESKTOP && <Segment compact className="flex-container-row flex-grow less-padding">
                        {this.renderOrganizationDropdown()}
                    </Segment>}
                    <TreeMenuRRC id="treeMenu" ref={this.refTreeMenu} root={AppMetaTempGlobals.appMetaInstance.menuEntries}
                        showSearchBar={!this.props.searchBarHasFocus} multiSearch={true}
                        onMenuItemClick={(isComposed: any) => !isComposed && this.props.dispatchers.setInReduxState({ drawerOpen: false })}
                        onSearchExpressionModified={() => this.refTreeMenuSearchWrapper.current?.forceUpdate()} initialExpandedIds={AppMetaTempGlobals.appMetaInstance.menuInitialExpandedIds} />
                </Drawer>
                {responsiveState.mode === ResponsiveMode.DESKTOP && organizations && initializationsForClient.currentUser && initializationsForClient.organizationSettings?.globalOrganizationFilter === OrganizationFilter.LEFT_SIDE
                    && props.root && !(props.root.children?.length == 1 && Utils.isNullOrEmpty(props.root.children[0].children)) ?
                    <div className="flex-container OrganizationTreeLeftArea gap5" data-testid="OrganizationTreeLeftBarContainer">
                        <Divider fitted />
                        <CombineCheck checked={this.props.combineCheck} filterLeftBarCheck={this.props.filterLeftBarCheck} fontSize={"3pt"}
                            onChange={async (checked: boolean) => {
                                if (checked) {
                                    await this.refOrganizationTree.current!.combineAll();
                                } else {
                                    await this.refOrganizationTree.current!.separateAll();
                                }
                                setCombineCheck(checked);
                                this.onChangeOrganizationDropdownValue(this.refOrganizationTree.current!.getCurrentOrganizationDropdownValue());
                            }}
                        />
                        <div className="tiny-margin-left tiny-margin-right"><ExpandCollapseButtons refOrganizationTree={this.refOrganizationTree} size="mini" /></div>
                        <Divider fitted />
                        <OrganizationTreeRRC id="OrganizationTreeLeftRRC" ref={this.refOrganizationTree} root={this.props.leftBarRoot} asLeftBar
                            organizationDropdownValue={{ ...initializationsForClient.currentOrganizationDropdownValue!, alsoIncludeOrganizations: initializationsForClient.allOrganizationsAccess ? getFilterLeftBarOrganizations() : initializationsForClient.currentOrganizationDropdownValue!.alsoIncludeOrganizations }}
                            combineCheck={this.props.combineCheck} filterLeftBarCheck={this.props.filterLeftBarCheck}
                            onSelectOrganizationItem={(organizationDropdownValue: OrganizationDropdownValue) => this.onChangeOrganizationDropdownValue(organizationDropdownValue)}
                        />
                    </div>
                    : null}
                <Suspense fallback={<div>Loading...</div>} >
                    <Switch location={locationForMainContainer}>
                        {Object.keys(eds).reduce((tableRoutes, ed) => {
                            tableRoutes.push(eds[ed].renderTableRoute());
                            return tableRoutes;
                        }, [] as JSX.Element[])}
                        {/**
                         * We need to exclude editors routes to avoid double rendering of editor page,
                         * without those editors which are not modals (e.g. editor for dashboard)
                         */}
                        {AppMetaTempGlobals.appMetaInstance.routes.filter(route => (route.props.path.includes("Editor/:id") && !route.props.routeIsModal) || !route.props.path.includes("Editor/:id"))}
                    </Switch>
                    {Object.values(entityDescriptors).map(ed => ed.renderInAppComponent())}
                    {AppMetaTempGlobals.appMetaInstance.enableModalRoutes()
                        ? <>
                            {props.locationForDrawer && <Drawer className="RouteDrawer" visible width={"80%"} closeIcon={<Button data-testid="Drawer_closeBtn" className="less-padding" icon="close" circular />}
                                onClose={() => {
                                    props.dispatchers.setInReduxState({ locationForDrawer: undefined });
                                    AppMetaTempGlobals.appMetaInstance.tempLocationForEditorBackground && props.dispatchers.dispatch(push(AppMetaTempGlobals.appMetaInstance.tempLocationForEditorBackground.pathname));
                                }}>
                                <Switch location={props.locationForDrawer}>
                                    {AppMetaTempGlobals.appMetaInstance.routes}
                                </Switch>
                            </Drawer>}
                            {this.renderLocationsModals(props)}
                        </>
                        : <Switch>
                            {AppMetaTempGlobals.appMetaInstance.routes}
                        </Switch>
                    }
                </Suspense>
                {alert}
                {this.renderAdditionalContent()}
            </ContainerWithHeader>
            <ModalExt open={props.currentUserModalOpen} onClose={() => props.dispatchers.setInReduxState({ currentUserModalOpen: false })}>
                <Modal.Header>{_msg("login.myAccount")}</Modal.Header>
                <Modal.Content><ConnectedComponentInSimpleComponent info={infoCurrentUserEditorPage} closeModal={() => props.dispatchers.setInReduxState({ currentUserModalOpen: false })} /></Modal.Content>
            </ModalExt>
        </RoutedModals></>);
    }


    private renderOrganizationDropdown(): React.ReactNode {
        const { initializationsForClient, responsiveState } = this.props.appContainerContextValue;
        const organization = initializationsForClient.currentOrganization;
        return <>
            <Dimmer active={this.props.organizationSelectorPopupOpen} />
            <Scriptable id={"organizationDropdownPopup"} onOpen={() => this.onOpenOrganizationDropdownPopup()}>{scriptable => <Popup position={responsiveState.mode !== ResponsiveMode.DESKTOP ? "bottom left" : "bottom right"} open={this.props.organizationSelectorPopupOpen} className="OrganizationDropdown_popup"
                trigger={
                    <div className="OrganizationDropdown flex-container-row flex-grow-shrink-no-overflow">
                        <Button data-testid="CurrentOrganization" basic color="black" className="flex-container-row flex-center flex-grow-shrink-no-overflow gap5 less-padding no-margin" >
                            {AppMetaTempGlobals.appMetaInstance.getOrganizationTreeIcon(organization?.id || NO_ORGANIZATION.key, organization || {}, organization, { className: "no-margin" }, true)}
                            <span className="flex-container-row flex-grow-shrink-no-overflow">{organization ? organization!.name : NO_ORGANIZATION.text}</span>
                            <Icon name="dropdown" className="no-margin" />
                        </Button>
                    </div>
                }
                content={<OrganizationSelectorRRC ref={this.refOrganizationSelector} id="OrganizationSelectorRRC" root={this.props.root} organizationDropdownValue={{ ...initializationsForClient.currentOrganizationDropdownValue!, alsoIncludeOrganizations: getFilterLeftBarOrganizations() }}
                    onApply={() => this.onChangeOrganizationDropdownValue(this.refOrganizationSelector.current?.getCurrentOrganizationDropdownValue())}
                    onSelectOrganizationItem={(organizationDropdownValue: OrganizationDropdownValue) => this.onChangeOrganizationDropdownValue(organizationDropdownValue)} />}
                on={"click"} onOpen={(e, data) => scriptable.proxy.onOpen()}
                onClose={(e, data) => this.onChangeOrganizationDropdownValue(this.refOrganizationSelector.current?.getCurrentOrganizationDropdownValue())}>
            </Popup>}
            </Scriptable>
        </>;
    }
}

export const APP_CONTAINER = "AppContainer";
export const infoAppContainer = new ConnectedPageInfo(sliceAppContainer, AppContainer, APP_CONTAINER);"./entity_crud/CrudSettings""./pages/role/roleEntityDescriptor""./utils/ColorRegistry""./pages/Audit/auditEntityDescriptor""./pages/user/CurrentUserEditorPage"